import React, { useCallback, useRef, useEffect } from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import { useIDEStore } from '@/stores/useIDEStore';
import { apiClient } from '@/services/api';

interface MonacoEditorWrapperProps {
  projectId: string;
}

const LANGUAGE_MAP: Record<string, string> = {
  typescript: 'typescript',
  typescriptreact: 'typescript',
  javascript: 'javascript',
  javascriptreact: 'javascript',
  css: 'css',
  html: 'html',
  json: 'json',
  markdown: 'markdown',
  python: 'python',
  yaml: 'yaml',
  shell: 'shell',
  sql: 'sql',
};

export default function MonacoEditorWrapper({ projectId }: MonacoEditorWrapperProps) {
  const openFiles = useIDEStore((s) => s.openFiles);
  const activeFile = useIDEStore((s) => s.activeFile);
  const updateFileContent = useIDEStore((s) => s.updateFileContent);
  const markFileSaved = useIDEStore((s) => s.markFileSaved);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const editorRef = useRef<any>(null);
  // Use refs for values that the Monaco action closure needs, so it's never stale
  const activeFileRef = useRef(activeFile);
  const projectIdRef = useRef(projectId);

  // Keep refs in sync with latest props/state
  useEffect(() => {
    activeFileRef.current = activeFile;
  }, [activeFile]);

  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const activeFileData = openFiles.find((f) => f.path === activeFile);

  const handleChange = useCallback(
    (value: string | undefined) => {
      const filePath = activeFileRef.current;
      if (!filePath || value === undefined) return;

      updateFileContent(filePath, value);

      // Debounced auto-save — capture filePath and value at call time
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        try {
          await apiClient.saveFileContent(projectIdRef.current, filePath, value);
          markFileSaved(filePath);
        } catch {
          // Save failed silently — file stays dirty
        }
      }, 1500);
    },
    [updateFileContent, markFileSaved]
  );

  const handleMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    // Ctrl/Cmd+S to save immediately — uses refs to avoid stale closure
    editor.addAction({
      id: 'save-file',
      label: 'Save File',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
      run: async (ed: any) => {
        const filePath = activeFileRef.current;
        if (!filePath) return;
        const content = ed.getValue();
        try {
          await apiClient.saveFileContent(projectIdRef.current, filePath, content);
          markFileSaved(filePath);
        } catch {
          // ignore
        }
      },
    });
  };

  if (!activeFileData) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        Select a file to edit
      </div>
    );
  }

  const language = LANGUAGE_MAP[activeFileData.language] || activeFileData.language || 'text';

  return (
    <Editor
      height="100%"
      language={language}
      value={activeFileData.content}
      onChange={handleChange}
      onMount={handleMount}
      theme="vs-dark"
      options={{
        minimap: { enabled: false },
        fontSize: 13,
        lineNumbers: 'on',
        wordWrap: 'on',
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        formatOnPaste: true,
      }}
    />
  );
}
